import React, { useEffect, useState } from "react";
import { X, Cookie, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";

const CONSENT_COOKIE = "ybk_cookie_consent";
const CONSENT_VERSION = "2"; // Bumped — adds granular categories. Re-prompts everyone on v1.
const COOKIE_DOMAIN = ".yoobikwitus.com"; // Covers both TLD and app subdomain
const POLICY_URL = "https://yoobikwitus.com/cookie-policy";

// Optional categories. Add new ones here as the app grows (e.g. error_monitoring for Sentry).
const CATEGORIES = [
  {
    key: "analytics",
    label: "Analytics",
    description:
      "Helps us understand how the app is being used so we can improve it. No personal data is shared with third parties. Currently not active.",
  },
  {
    key: "error_monitoring",
    label: "Error monitoring",
    description:
      "Sends anonymised crash and error reports so we can fix bugs faster. Not currently active.",
  },
];

// Cookie helpers — set on the root domain so consent is shared across subdomains
function readConsent() {
  try {
    const match = document.cookie
      .split("; ")
      .find((c) => c.startsWith(`${CONSENT_COOKIE}=`));
    if (!match) return null;
    return JSON.parse(decodeURIComponent(match.split("=")[1]));
  } catch {
    return null;
  }
}

function writeConsent(value) {
  const encoded = encodeURIComponent(JSON.stringify(value));
  const maxAge = 60 * 60 * 24 * 365; // 1 year
  document.cookie = [
    `${CONSENT_COOKIE}=${encoded}`,
    `Domain=${COOKIE_DOMAIN}`,
    `Max-Age=${maxAge}`,
    "Path=/",
    "SameSite=Lax",
    "Secure",
  ].join("; ");
}

function defaultCategoryState(value) {
  return CATEGORIES.reduce((acc, c) => ({ ...acc, [c.key]: value }), {});
}

export function useConsent() {
  const consent = readConsent();
  const hasDecided = Boolean(consent && consent.v === CONSENT_VERSION);
  const categories = hasDecided
    ? { ...defaultCategoryState(false), ...consent.categories }
    : defaultCategoryState(false);
  return {
    hasDecided,
    categories,
    // Back-compat for any existing code checking analytics directly
    analytics: categories.analytics,
  };
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [categories, setCategories] = useState(defaultCategoryState(false));

  useEffect(() => {
    const existing = readConsent();
    if (!existing || existing.v !== CONSENT_VERSION) {
      // Small delay so the banner doesn't pop immediately on top of page load
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  if (!visible) return null;

  const save = (categoriesToSave) => {
    writeConsent({
      v: CONSENT_VERSION,
      essential: true,
      categories: categoriesToSave,
      ts: Date.now(),
    });
    setVisible(false);
  };

  const acceptAll = () => save(defaultCategoryState(true));
  const essentialOnly = () => save(defaultCategoryState(false));
  const savePreferences = () => save(categories);

  const toggleCategory = (key) => {
    setCategories((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Cookie consent"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:w-[420px] z-[9999]"
    >
      <div className="rounded-xl border border-[#272A30] bg-[#111318] shadow-2xl shadow-black/60 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#272A30]">
          <div className="flex items-center gap-2.5">
            <Cookie className="h-4 w-4 text-amber-400" />
            <span className="text-sm font-semibold tracking-tight">Cookie settings</span>
          </div>
          <button
            onClick={essentialOnly}
            aria-label="Decline and close"
            className="text-zinc-500 hover:text-white transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          <p className="text-sm text-zinc-300 leading-relaxed">
            We use cookies to keep you signed in and remember your preferences. We do not
            use advertising cookies or sell your data.
          </p>

          {/* Cookie breakdown — collapsible, with per-category toggles */}
          <div>
            <button
              onClick={() => setExpanded((e) => !e)}
              className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition-colors"
            >
              {expanded ? (
                <ChevronUp className="h-3.5 w-3.5" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5" />
              )}
              {expanded ? "Hide details" : "Customise"}
            </button>

            {expanded && (
              <div className="mt-3 space-y-3">
                <CookieRow
                  label="Essential"
                  tag="Always on"
                  tagColor="text-emerald-400 border-emerald-500/30 bg-emerald-500/10"
                  description="Keeps you logged in across page refreshes. Stored in your browser's local storage on this device only. Required for the app to function."
                />
                {CATEGORIES.map((c) => (
                  <CookieRow
                    key={c.key}
                    label={c.label}
                    description={c.description}
                    toggle={{
                      checked: categories[c.key],
                      onChange: () => toggleCategory(c.key),
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          <a
            href={POLICY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
          >
            Full cookie policy
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>

        {/* Actions */}
        <div className="px-5 pb-5 flex gap-2">
          {expanded ? (
            <button
              onClick={savePreferences}
              className="flex-1 rounded-lg bg-white text-[#0A0A0B] font-semibold text-sm px-4 py-2.5 hover:bg-zinc-200 transition-colors"
            >
              Save preferences
            </button>
          ) : (
            <>
              <button
                onClick={essentialOnly}
                className="flex-1 rounded-lg border border-[#272A30] px-4 py-2.5 text-sm text-zinc-300 hover:bg-[#1A1D24] transition-colors"
              >
                Essential only
              </button>
              <button
                onClick={acceptAll}
                className="flex-1 rounded-lg bg-white text-[#0A0A0B] font-semibold text-sm px-4 py-2.5 hover:bg-zinc-200 transition-colors"
              >
                Accept all
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function CookieRow({ label, tag, tagColor, description, toggle }) {
  return (
    <div className="rounded-lg border border-[#272A30] bg-[#0A0A0B] p-3 space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-zinc-200">{label}</span>
        {toggle ? (
          <button
            role="switch"
            aria-checked={toggle.checked}
            aria-label={`Toggle ${label} cookies`}
            onClick={toggle.onChange}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
              toggle.checked ? "bg-emerald-500" : "bg-zinc-700"
            }`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                toggle.checked ? "translate-x-[18px]" : "translate-x-1"
              }`}
            />
          </button>
        ) : (
          <span
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${tagColor}`}
          >
            {tag}
          </span>
        )}
      </div>
      <p className="text-[11px] text-zinc-500 leading-relaxed">{description}</p>
    </div>
  );
}
